<template>
  <div
    class="min-h-screen status-page"
    :class="themeClass"
    :style="{
      backgroundColor: pageBackgroundColor,
      '--primary-color': primaryColor,
      '--background-color': backgroundColor,
      '--text-color': textColor
    }"
  >
    <!-- Custom HTML Head Content -->
    <div v-if="statusPage?.branding?.customHtml" v-html="statusPage.branding.customHtml" class="hidden"></div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center min-h-screen">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2" :style="{ borderColor: primaryColor }"></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <h1 class="text-2xl font-bold text-gray-900 mb-4">Status Page Not Found</h1>
        <p class="text-gray-600">The requested status page could not be found.</p>
      </div>
    </div>

    <!-- Status Page Content -->
    <div v-else-if="statusPage">
      <!-- Header -->
      <header class="text-white status-header" :style="{ backgroundColor: headerBackgroundColor }">
        <div class="max-w-4xl mx-auto px-6 py-8">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <img
                v-if="statusPage.branding?.logoUrl"
                :src="statusPage.branding.logoUrl"
                :alt="statusPage.name"
                class="h-8 w-8 rounded"
              />
              <div v-else class="h-8 w-8 bg-white bg-opacity-20 rounded flex items-center justify-center">
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm0 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clip-rule="evenodd" />
                </svg>
              </div>
              <h1 class="text-2xl font-bold">{{ statusPage.name }}</h1>
            </div>
            <div class="flex items-center space-x-3">
              <button
                class="text-white text-sm font-medium"
                @click="reportProblem"
              >
                Report A Problem
              </button>
              <button
                class="text-white px-4 py-2 rounded text-sm font-medium"
                :style="{ backgroundColor: subscribeButtonColor }"
                @click="showSubscribeModal = true"
              >
                Subscribe to updates
              </button>
            </div>
          </div>
        </div>
      </header>

      <!-- Main Content -->
      <main class="max-w-4xl mx-auto px-6 py-8">
        <!-- Overall Status Banner -->
        <div :class="[
          'rounded-lg px-6 py-4 mb-8 text-white font-medium text-lg status-banner',
          overallStatusClass
        ]">
          {{ overallStatusText }}
          <div class="text-sm opacity-90 mt-1">
            All times shown in {{ statusPage?.timezone || 'UTC' }}
          </div>
        </div>

        <!-- Components Section -->
        <div class="space-y-6 mb-12">
          <div class="flex items-center justify-between">
            <p class="text-gray-600 text-sm">
              Response time over the past 60 days.
              <a href="#" class="text-blue-600">View historical uptime.</a>
            </p>
          </div>

          <div v-if="groupedComponents.length === 0" class="text-center py-8">
            <p class="text-gray-500">No services are being monitored yet.</p>
          </div>

          <div v-else class="space-y-8">
            <!-- Component Groups -->
            <div
              v-for="(group, groupIndex) in groupedComponents"
              :key="groupIndex"
              class="border rounded-lg overflow-hidden component-card"
              :style="{ borderColor: borderColor }"
            >
              <!-- Group Header -->
              <div
                class="flex items-center justify-between p-4"
                :style="{ backgroundColor: cardBackgroundColor }"
              >
                <div class="flex items-center space-x-2">
                  <h3 class="text-lg font-medium" :style="{ color: textColor }">{{ group.name || 'Services' }}</h3>
                </div>
                <span :class="[
                  'px-3 py-1 rounded text-sm font-medium',
                  getGroupStatusClass(group.components)
                ]">
                  {{ getGroupStatusText(group.components) }}
                </span>
              </div>

              <!-- Group Components -->
              <div :style="{ backgroundColor: sectionBackgroundColor }">
                <div
                  v-for="component in group.components"
                  :key="component.id"
                  class="border-t p-4 component-card"
                  :style="{
                    borderColor: borderColor,
                    backgroundColor: cardBackgroundColor
                  }"
                >
                  <div class="flex items-center justify-between mb-3">
                    <h4 class="text-lg font-medium" :style="{ color: textColor }">{{ component.name }}</h4>
                    <span :class="[
                      'px-3 py-1 rounded text-sm font-medium',
                      getComponentStatusClass(component.status)
                    ]">
                      {{ getComponentStatusText(component.status) }}
                    </span>
                  </div>

                  <!-- Component content (show immediately) -->
                  <div>
                    <!-- Response Time Chart -->
                    <div class="mb-4">
                      <div class="flex justify-between items-center mb-2">
                        <span class="text-sm text-gray-600">60 days ago</span>
                        <span class="text-sm text-gray-600">Today</span>
                      </div>

                      <!-- Response Time Line Graph Container -->
                      <div class="relative response-time-chart-container">
                        <ResponseTimeChart
                          :data="generateResponseTimeData(component)"
                          :width="800"
                          :height="80"
                          :primary-color="primaryColor"
                        />
                      </div>
                    </div>

                    <!-- Component Description -->
                    <p v-if="component.description" class="text-sm mb-2" :style="{ color: mutedTextColor }">
                      {{ component.description }}
                    </p>

                    <!-- Component Stats -->
                    <div class="flex space-x-4 text-sm" :style="{ color: mutedTextColor }">
                      <span>Avg Response: {{ (component.stats?.avgResponseTime || 150).toFixed(0) }}ms</span>
                      <span>24h: {{ (component.stats?.uptime24h * 100 || 99.9).toFixed(1) }}%</span>
                      <span>7d: {{ (component.stats?.uptime7d * 100 || 99.8).toFixed(1) }}%</span>
                      <span>30d: {{ (component.stats?.uptime30d * 100 || 99.5).toFixed(1) }}%</span>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Scheduled Maintenance Section -->
        <section class="mb-12" v-if="scheduledMaintenances && scheduledMaintenances.length > 0">
          <h2 class="text-2xl font-bold mb-6" :style="{ color: textColor }">Scheduled Maintenance</h2>

          <div class="space-y-6">
            <div
              v-for="maintenance in scheduledMaintenances"
              :key="maintenance.id"
              class="border rounded-lg p-6"
              :style="{ borderColor: borderColor, backgroundColor: cardBackgroundColor }"
            >
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <h3 class="text-lg font-semibold mb-2" :style="{ color: textColor }">{{ maintenance.title }}</h3>
                  <p class="text-sm mb-4" :style="{ color: mutedTextColor }">{{ maintenance.description }}</p>

                  <div class="flex items-center space-x-4 text-sm mb-4" :style="{ color: mutedTextColor }">
                    <span>
                      <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {{ formatMaintenanceDate(maintenance.startTime) }} - {{ formatMaintenanceDate(maintenance.endTime) }}
                    </span>
                    <span :class="[
                      'px-2 py-1 rounded text-xs font-medium',
                      getMaintenanceStatusClass(maintenance.status)
                    ]">
                      {{ getMaintenanceStatusText(maintenance.status) }}
                    </span>
                  </div>

                  <div v-if="maintenance.affectedComponents && maintenance.affectedComponents.length > 0" class="mb-4">
                    <p class="text-sm font-medium mb-2" :style="{ color: textColor }">Affected Components:</p>
                    <div class="flex flex-wrap gap-2">
                      <span
                        v-for="component in maintenance.affectedComponents"
                        :key="component"
                        class="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                      >
                        {{ component }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Past Incidents Section -->
        <section class="mb-12">
           <h2 class="text-2xl font-bold mb-6" :style="{ color: textColor }">Past Incidents</h2>

           <div class="space-y-6">
             <div v-if="incidents.length === 0 && !incidentsLoading" class="text-center py-8">
               <p :style="{ color: mutedTextColor }">No incidents reported.</p>
             </div>
             <div v-else-if="incidentsLoading && incidents.length === 0" class="text-center py-8">
               <div class="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" :style="{ borderColor: primaryColor }"></div>
               <p class="mt-4" :style="{ color: mutedTextColor }">Loading incidents...</p>
             </div>
             <div v-else>
               <div v-for="incident in incidents" :key="incident.id" class="border rounded-lg overflow-hidden" :style="{ borderColor: borderColor, backgroundColor: cardBackgroundColor }">
                 <!-- Incident Header (always visible) -->
                 <div class="p-4 cursor-pointer" @click="toggleIncidentExpansion(incident.id)">
                   <div class="flex items-start justify-between">
                     <div class="flex-1">
                       <h3 class="text-lg font-semibold mb-2" :style="{ color: textColor }">{{ incident.title }}</h3>
                       <div class="flex items-center space-x-4 text-sm" :style="{ color: mutedTextColor }">
                         <span>{{ formatDate(incident.createdAt) }}</span>
                         <span :class="[
                           'px-2 py-1 rounded text-xs font-medium',
                           getIncidentSeverityClass(incident.severity)
                         ]">
                           {{ getIncidentSeverityText(incident.severity) }}
                         </span>
                         <span :class="[
                           'px-2 py-1 rounded text-xs font-medium',
                           getIncidentStatusClass(incident.status)
                         ]">
                           {{ getIncidentStatusText(incident.status) }}
                         </span>
                       </div>
                     </div>
                     <div class="flex items-center space-x-2">
                       <svg
                         :class="['w-5 h-5 transition-transform', expandedIncidents.has(incident.id) ? 'rotate-180' : '']"
                         fill="none"
                         stroke="currentColor"
                         viewBox="0 0 24 24"
                         :style="{ color: mutedTextColor }"
                       >
                         <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                       </svg>
                     </div>
                   </div>
                 </div>

                 <!-- Incident Details (collapsible) -->
                 <div v-if="expandedIncidents.has(incident.id)" class="px-4 pb-4 border-t" :style="{ borderColor: borderColor }">
                   <p class="text-sm mb-4" :style="{ color: mutedTextColor }">{{ incident.description }}</p>

                   <!-- Incident Updates -->
                   <div v-if="incident.updates && incident.updates.length > 0" class="space-y-3">
                     <h4 class="text-sm font-medium" :style="{ color: textColor }">Updates</h4>
                     <div v-for="update in incident.updates" :key="update.id" class="pl-4 border-l-2" :style="{ borderColor: primaryColor }">
                       <div class="text-xs" :style="{ color: mutedTextColor }">{{ formatDate(update.createdAt) }}</div>
                       <p class="text-sm mt-1" :style="{ color: textColor }">{{ update.message }}</p>
                       <span :class="[
                         'inline-block px-2 py-1 mt-2 text-xs rounded',
                         getIncidentStatusClass(update.status)
                       ]">
                         {{ getIncidentStatusText(update.status) }}
                       </span>
                     </div>
                   </div>
                 </div>
               </div>

               <!-- Load More Button -->
               <div v-if="hasMoreIncidents || incidentsLoading" class="text-center mt-8">
                 <button
                   v-if="!incidentsLoading"
                   @click="loadMoreIncidents"
                   class="px-6 py-3 text-white rounded-lg font-medium transition-colors"
                   :style="{ backgroundColor: primaryColor }"
                   :disabled="incidentsLoading"
                 >
                   Load More Incidents
                 </button>
                 <div v-else class="flex items-center justify-center space-x-2">
                   <div class="animate-spin rounded-full h-6 w-6 border-b-2" :style="{ borderColor: primaryColor }"></div>
                   <span :style="{ color: mutedTextColor }">Loading...</span>
                 </div>
               </div>
             </div>
           </div>
         </section>
      </main>
    </div>

    <!-- Tooltip -->
    <div
      v-if="tooltip.show"
      :style="{
        left: tooltip.x + 'px',
        top: tooltip.y + 'px',
        backgroundColor: tooltipBackgroundColor,
        color: tooltipTextColor
      }"
      class="fixed z-50 text-xs rounded px-2 py-1 pointer-events-none"
    >
      {{ tooltip.content }}
    </div>

    <!-- Password Prompt Modal -->
    <div v-if="showPasswordPrompt" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="rounded-lg p-6 w-full max-w-md mx-4" :style="{ backgroundColor: cardBackgroundColor }">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold" :style="{ color: textColor }">Private Status Page</h3>
          <button @click="showPasswordPrompt = false" :style="{ color: mutedTextColor }">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <p class="mb-4" :style="{ color: mutedTextColor }">This status page is private. Please enter the password to access it.</p>
        <form @submit.prevent="authenticate">
          <div class="mb-4">
            <input
              v-model="password"
              type="password"
              required
              placeholder="Enter password"
              class="w-full px-3 py-2 border rounded-md focus:ring-2 focus:border-transparent"
              :style="{
                borderColor: passwordError ? '#EF4444' : borderColor,
                backgroundColor: cardBackgroundColor,
                color: textColor,
                '--focus-ring-color': passwordError ? '#EF4444' : primaryColor
              }"
            />
            <p v-if="passwordError" class="text-red-500 text-sm mt-1">{{ passwordError }}</p>
          </div>
          <div class="flex justify-end space-x-3">
            <button
              type="button"
              @click="showPasswordPrompt = false"
              class="px-4 py-2"
              :style="{ color: mutedTextColor }"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="authenticating"
              class="px-4 py-2 text-white rounded disabled:opacity-50"
              :style="{ backgroundColor: primaryColor }"
            >
              {{ authenticating ? 'Authenticating...' : 'Access Page' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Subscribe Modal -->
    <div v-if="showSubscribeModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="rounded-lg p-6 w-full max-w-md mx-4" :style="{ backgroundColor: cardBackgroundColor }">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold" :style="{ color: textColor }">Subscribe to Updates</h3>
          <button @click="showSubscribeModal = false" :style="{ color: mutedTextColor }">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <p class="mb-4" :style="{ color: mutedTextColor }">Get notified when we post updates about incidents and maintenance.</p>
        <form @submit.prevent="subscribe">
          <div class="mb-4">
            <input
              v-model="subscriptionEmail"
              type="email"
              required
              placeholder="Enter your email address"
              class="w-full px-3 py-2 border rounded-md focus:ring-2 focus:border-transparent"
              :style="{
                borderColor: borderColor,
                backgroundColor: cardBackgroundColor,
                color: textColor,
                '--focus-ring-color': primaryColor
              }"
            />
            <!-- Honeypot field -->
            <input v-model="honeypot" type="text" tabindex="-1" autocomplete="off" class="hidden" aria-hidden="true" />
          </div>
          <div class="flex justify-end space-x-3">
            <button
              type="button"
              @click="showSubscribeModal = false"
              class="px-4 py-2"
              :style="{ color: mutedTextColor }"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="subscribing"
              class="px-4 py-2 text-white rounded disabled:opacity-50"
              :style="{ backgroundColor: primaryColor }"
            >
              {{ subscribing ? 'Subscribing...' : 'Subscribe' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import ResponseTimeChart from '@/components/charts/ResponseTimeChart.vue'

const route = useRoute()

const statusPage = ref<any>(null)
const incidents = ref<any[]>([])
const incidentsLoading = ref(false)
const incidentsPage = ref(1)
const incidentsPageSize = ref(20)
const hasMoreIncidents = ref(true)
const incidentsDays = ref(0) // 0 means all incidents
const scheduledMaintenances = ref<any[]>([])
const loading = ref(true)
const error = ref(false)
const subscriptionEmail = ref('')
const subscribing = ref(false)
const pageSessionToken = ref<string | null>(null)
const subscriptionToken = ref<string | null>(null)
const honeypot = ref('')
const pageLoadStartedAt = Date.now()
const passwordRequired = ref(false)
const password = ref('')
const passwordError = ref('')
const authenticating = ref(false)
const showSubscribeModal = ref(false)
const showPasswordPrompt = ref(false)

// Incident expansion state
const expandedIncidents = ref(new Set<string>())

// Tooltip state
const tooltip = ref({
  show: false,
  x: 0,
  y: 0,
  content: ''
})

// Cache for real response time data from backend
const responseTimeDataCache = new Map<string, any[]>()
const historyDataCache = ref<any>(null)

// Component state (simplified)


const props = defineProps<{
  slug?: string
  isDomain?: string
  domain?: string
}>()

// Group components by their group property
const groupedComponents = computed(() => {
  if (!statusPage.value?.components) return []

  const groups = new Map<string, any[]>()

  statusPage.value.components.forEach((component: any) => {
    const groupName = component.group || 'Services'
    if (!groups.has(groupName)) {
      groups.set(groupName, [])
    }
    groups.get(groupName)!.push(component)
  })

  return Array.from(groups.entries()).map(([name, components]) => ({
    name,
    components: components.sort((a, b) => (a.order || 0) - (b.order || 0))
  }))
})

// Overall status computed properties
const overallStatusText = computed(() => {
  switch (statusPage.value?.overallStatus) {
    case 1: return 'All Systems Operational'
    case 2: return 'Minor Issues'
    case 3: return 'Major Issues'
    case 4: return 'Critical Issues'
    case 5: return 'Under Maintenance'
    default: return 'Status Unknown'
  }
})

const overallStatusClass = computed(() => {
  const status = statusPage.value?.overallStatus
  switch (status) {
    case 1: return 'bg-green-500'
    case 2: return 'bg-yellow-500'
    case 3: return 'bg-orange-500'
    case 4: return 'bg-red-500'
    case 5: return 'bg-blue-500'
    default: return 'bg-gray-500'
  }
})

// Dynamic theming computed properties based on branding
const primaryColor = computed(() => statusPage.value?.branding?.primaryColor || '#3B82F6')
const backgroundColor = computed(() => statusPage.value?.branding?.backgroundColor || '#FFFFFF')
const textColor = computed(() => statusPage.value?.branding?.textColor || '#1F2937')

// Derived theme colors
const pageBackgroundColor = computed(() => {
  // Slightly darker than background for page
  const bg = backgroundColor.value
  if (bg === '#FFFFFF') return '#F9FAFB' // gray-50
  return bg
})

const headerBackgroundColor = computed(() => {
  // Use primary color for header
  return primaryColor.value
})

const subscribeButtonColor = computed(() => {
  // Use a complementary color for subscribe button (orange-ish)
  return '#F97316' // orange-500
})

const cardBackgroundColor = computed(() => {
  return backgroundColor.value
})

const borderColor = computed(() => {
  // Light border color based on text color
  const text = textColor.value
  if (text === '#1F2937') return '#E5E7EB' // gray-200
  return text + '20' // Add transparency
})

const mutedTextColor = computed(() => {
  // Muted version of text color
  const text = textColor.value
  if (text === '#1F2937') return '#6B7280' // gray-500
  return text + 'CC' // Add some transparency
})

const sectionBackgroundColor = computed(() => {
  // Slightly different from card background
  const bg = backgroundColor.value
  if (bg === '#FFFFFF') return '#F9FAFB' // gray-50
  return bg
})

const hoverBackgroundColor = computed(() => {
  // Slightly darker for hover
  const bg = backgroundColor.value
  if (bg === '#FFFFFF') return '#F9FAFB' // gray-50
  return bg
})

const tooltipBackgroundColor = computed(() => {
  // Dark tooltip background
  return '#1F2937' // gray-800
})

const tooltipTextColor = computed(() => {
  return '#FFFFFF'
})

// Theme class from settings to enable rich theming via CSS
const themeClass = computed(() => {
  const theme = (statusPage.value?.settings?.theme || 'modern').toString().toLowerCase()
  return `theme-${theme}`
})


// Status helper functions
function getComponentStatusText(status: number) {
  switch (status) {
    case 1: return 'Operational'
    case 2: return 'Degraded Performance'
    case 3: return 'Partial Outage'
    case 4: return 'Major Outage'
    case 5: return 'Under Maintenance'
    default: return 'Unknown'
  }
}

function getComponentStatusClass(status: number) {
  switch (status) {
    case 1: return 'bg-green-100 text-green-800'
    case 2: return 'bg-yellow-100 text-yellow-800'
    case 3: return 'bg-orange-100 text-orange-800'
    case 4: return 'bg-red-100 text-red-800'
    case 5: return 'bg-blue-100 text-blue-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

function getGroupStatusText(components: any[]) {
  const statuses = components.map(c => c.status)
  if (statuses.every(s => s === 1)) return 'Operational'
  if (statuses.some(s => s === 4)) return 'Major Outage'
  if (statuses.some(s => s === 3)) return 'Partial Outage'
  if (statuses.some(s => s === 2)) return 'Degraded Performance'
  if (statuses.some(s => s === 5)) return 'Under Maintenance'
  return 'Mixed Status'
}

function getGroupStatusClass(components: any[]) {
  const statuses = components.map(c => c.status)
  if (statuses.every(s => s === 1)) return 'bg-green-100 text-green-800'
  if (statuses.some(s => s === 4)) return 'bg-red-100 text-red-800'
  if (statuses.some(s => s === 3)) return 'bg-orange-100 text-orange-800'
  if (statuses.some(s => s === 2)) return 'bg-yellow-100 text-yellow-800'
  if (statuses.some(s => s === 5)) return 'bg-blue-100 text-blue-800'
  return 'bg-gray-100 text-gray-800'
}

function getIncidentStatusText(status: number) {
  switch (status) {
    case 1: return 'Investigating'
    case 2: return 'Identified'
    case 3: return 'Monitoring'
    case 4: return 'Resolved'
    default: return 'Unknown'
  }
}

function getIncidentStatusClass(status: number) {
  switch (status) {
    case 1: return 'bg-red-100 text-red-800'
    case 2: return 'bg-orange-100 text-orange-800'
    case 3: return 'bg-yellow-100 text-yellow-800'
    case 4: return 'bg-green-100 text-green-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

function getIncidentSeverityText(severity: number) {
  switch (severity) {
    case 1: return 'Minor'
    case 2: return 'Major'
    case 3: return 'Critical'
    default: return 'Unknown'
  }
}

function getIncidentSeverityClass(severity: number) {
  switch (severity) {
    case 1: return 'bg-yellow-100 text-yellow-800'
    case 2: return 'bg-orange-100 text-orange-800'
    case 3: return 'bg-red-100 text-red-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

function getMaintenanceStatusText(status: number) {
  switch (status) {
    case 1: return 'Scheduled'
    case 2: return 'In Progress'
    case 3: return 'Completed'
    case 4: return 'Cancelled'
    default: return 'Unknown'
  }
}

function getMaintenanceStatusClass(status: number) {
  switch (status) {
    case 1: return 'bg-blue-100 text-blue-800'
    case 2: return 'bg-yellow-100 text-yellow-800'
    case 3: return 'bg-green-100 text-green-800'
    case 4: return 'bg-gray-100 text-gray-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

async function loadHistoryData() {
  if (!statusPage.value?.slug || historyDataCache.value) return

  try {
    const apiBase = resolveApiBase()
    const apiUrl = `${apiBase}/public/status/${statusPage.value.slug}/history?days=60`
    const response = await fetch(apiUrl)

    if (response.ok) {
      historyDataCache.value = await response.json()
      console.log('Loaded real history data for status page')
    } else {
      console.warn('Failed to load history data, will use fallback')
    }
  } catch (err) {
    console.error('Error loading history data:', err)
  }
}

function generateResponseTimeData(component: any) {
  // Create cache key based on component ID
  const cacheKey = `response_${component.id || 'unknown'}`

  // Check if data is already cached
  if (responseTimeDataCache.has(cacheKey)) {
    return responseTimeDataCache.get(cacheKey)!
  }

  // Try to get real data from history cache
  if (historyDataCache.value?.data) {
    const componentHistory = historyDataCache.value.data
      .map((day: any) => {
        const compData = day.components?.find((c: any) => c.componentId === component.id)
        if (compData) {
          return {
            date: day.date,
            responseTime: compData.avgResponseTime || 150 + Math.random() * 50
          }
        }
        return null
      })
      .filter((item: any) => item !== null)

    if (componentHistory.length > 0) {
      // Cache the real data
      responseTimeDataCache.set(cacheKey, componentHistory)
      return componentHistory
    }
  }

  // Fallback: Generate mock response time data
  const data = []
  const today = new Date()

  // Use component ID as seed for consistent random values
  const seed = component.id ? component.id.split('').reduce((a: number, b: string) => a + b.charCodeAt(0), 0) : 1

  for (let i = 59; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(date.getDate() - i)

    // Generate consistent response time data based on component status
    let baseResponseTime = 150 // Base response time in ms
    if (component.status === 2) baseResponseTime = 300 // Degraded Performance - higher response time
    else if (component.status === 3) baseResponseTime = 500 // Partial Outage - much higher
    else if (component.status === 4) baseResponseTime = 2000 // Major Outage - very high
    else if (component.status === 5) baseResponseTime = 250 // Under Maintenance - slightly higher

    // Add small consistent variation based on day and component ID
    const variation = ((seed + i) % 20) - 10 // -10 to +10 ms variation
    const responseTime = Math.max(50, baseResponseTime + variation)

    data.push({
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      responseTime: responseTime
    })
  }

  // Cache the fallback data
  responseTimeDataCache.set(cacheKey, data)

  return data
}


function formatDate(dateString: string) {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

function formatMaintenanceDate(dateString: string) {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

function resolveApiBase(): string {
  const envBase = (import.meta as any).env?.VITE_PUBLIC_STATUS_API_BASE as string | undefined
  return (envBase ? envBase.replace(/\/$/, '') : 'http://localhost:5010/api')
}

async function loadStatusPage(passwordParam?: string) {
  try {
    const apiBase = resolveApiBase()
    let apiUrl = `${apiBase}/public/status`

    // Determine how to load the status page based on window location
    const host = window.location.hostname
    if (host !== 'localhost' && host !== '127.0.0.1' && !host.includes('3001')) {
      const parts = host.split('.')
      if (parts.length >= 3 && parts[1] === 'pingalert') {
        apiUrl += `/subdomain/${parts[0]}`
      } else {
        apiUrl += '/by-domain'
      }
    } else if (props.isDomain === 'true' && props.domain) {
      apiUrl += '/by-domain'
    } else if (props.slug) {
      apiUrl += `/${props.slug}`
    } else if (route.params.slug) {
      apiUrl += `/${route.params.slug as string}`
    } else {
      apiUrl += '/demo-status'
    }

    // Add password parameter if provided
    if (passwordParam) {
      apiUrl += `?password=${encodeURIComponent(passwordParam)}`
    }

    console.log('Loading status page from:', apiUrl)
    const response = await fetch(apiUrl)

    if (response.status === 401) {
      // Private page requires password
      showPasswordPrompt.value = true
      return
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const body = await response.json()
    pageSessionToken.value = body.pageSessionToken || null
    subscriptionToken.value = null
    statusPage.value = body
    scheduledMaintenances.value = statusPage.value.scheduledMaintenances || []

    // Load incidents for this status page
    await loadIncidents()

    // Load historical data for uptime charts
    await loadHistoryData()

  } catch (err) {
    error.value = true
    console.error('Failed to load status page:', err)
  } finally {
    loading.value = false
  }
}

async function loadIncidents(reset = true) {
  if (!statusPage.value?.slug) return

  if (reset) {
    incidentsPage.value = 1
    incidents.value = []
    hasMoreIncidents.value = true
  }

  if (!hasMoreIncidents.value && !reset) return

  incidentsLoading.value = true

  try {
    const apiBase = resolveApiBase()
    const apiUrl = `${apiBase}/public/status/${statusPage.value.slug}/incidents?days=${incidentsDays.value}&page=${incidentsPage.value}&pageSize=${incidentsPageSize.value}`
    const response = await fetch(apiUrl)

    if (response.ok) {
      const newIncidents = await response.json()
      if (newIncidents.length < incidentsPageSize.value) {
        hasMoreIncidents.value = false
      }

      if (reset) {
        incidents.value = newIncidents
      } else {
        incidents.value = [...incidents.value, ...newIncidents]
      }

      if (!reset) {
        incidentsPage.value++
      }
    } else {
      console.warn('Failed to load incidents:', response.status)
      if (reset) {
        incidents.value = []
      }
      hasMoreIncidents.value = false
    }
  } catch (err) {
    console.error('Failed to load incidents:', err)
    if (reset) {
      incidents.value = []
    }
    hasMoreIncidents.value = false
  } finally {
    incidentsLoading.value = false
  }
}

async function loadMoreIncidents() {
  if (!incidentsLoading.value) {
    await loadIncidents(false)
  }
}

async function authenticate() {
  if (!password.value) return

  passwordError.value = ''
  authenticating.value = true

  try {
    await loadStatusPage(password.value)
    if (statusPage.value) {
      showPasswordPrompt.value = false
      password.value = ''
    } else {
      passwordError.value = 'Invalid password'
    }
  } catch (err) {
    passwordError.value = 'Authentication failed'
    console.error('Authentication error:', err)
  } finally {
    authenticating.value = false
  }
}

function getCsrfToken(): string | null {
  return subscriptionToken.value
}

async function subscribe() {
  if (!subscriptionEmail.value || !statusPage.value) return

  subscribing.value = true
  try {
    const apiBase = resolveApiBase()
    const elapsed = Date.now() - pageLoadStartedAt
    if (elapsed < 800) {
      await new Promise(resolve => setTimeout(resolve, 800 - elapsed))
    }

    if (!subscriptionToken.value) {
      const tokenUrl = `${apiBase}/public/status/${statusPage.value.slug}/subscription-token`
      const tokenResponse = await fetch(tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pageSessionToken: pageSessionToken.value,
          honeypot: honeypot.value
        })
      })
      if (!tokenResponse.ok) {
        throw new Error('Unable to start subscription. Please reload and try again.')
      }
      const tokenBody = await tokenResponse.json()
      subscriptionToken.value = tokenBody.subscriptionToken || null
    }

    const apiUrl = `${apiBase}/public/status/${statusPage.value.slug}/subscribe`
    const csrfToken = getCsrfToken()
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken ? { 'X-Status-Token': csrfToken } : {})
      },
      body: JSON.stringify({
        email: subscriptionEmail.value,
        honeypot: honeypot.value,
        subscriptionToken: csrfToken,
        components: [],
        preferences: {
          incidents: true,
          maintenances: true,
          emailNotifications: true,
          smsNotifications: false,
          components: []
        }
      })
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const result = await response.json()
    alert(result.message || 'Successfully subscribed!')
    subscriptionEmail.value = ''
    showSubscribeModal.value = false
  } catch (error) {
    console.error('Subscription error:', error)
    alert('Failed to subscribe. Please try again.')
  } finally {
    subscribing.value = false
  }
}

function reportProblem() {
  // This could open a modal or redirect to a form
  alert('Report problem functionality would be implemented here')
}

// Incident expansion functions
function toggleIncidentExpansion(incidentId: string) {
  if (expandedIncidents.value.has(incidentId)) {
    expandedIncidents.value.delete(incidentId)
  } else {
    expandedIncidents.value.add(incidentId)
  }
}

// Tooltip functionality
function showTooltip(event: MouseEvent, day: any) {
  tooltip.value = {
    show: true,
    x: event.clientX + 10,
    y: event.clientY - 30,
    content: `${day.date}: ${(day.uptime * 100).toFixed(1)}% uptime`
  }
}

function hideTooltip() {
  tooltip.value.show = false
}

// Simplified chart functions (no virtual scrolling needed)


// Update favicon when status page loads
function updateFavicon() {
  if (statusPage.value?.branding?.faviconUrl) {
    const favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement
    if (favicon) {
      favicon.href = statusPage.value.branding.faviconUrl
    }
  }
}

// Inject custom CSS into document head
function injectCustomCss() {
  // Remove existing custom style tag
  const existingStyle = document.getElementById('status-page-custom-css')
  if (existingStyle) {
    existingStyle.remove()
  }

  // Add new custom CSS if available
  if (statusPage.value?.branding?.customCss) {
    const style = document.createElement('style')
    style.id = 'status-page-custom-css'
    style.textContent = statusPage.value.branding.customCss
    document.head.appendChild(style)
  }
}

// Watch for status page changes to update favicon and custom CSS
watch(statusPage, () => {
  if (statusPage.value) {
    updateFavicon()
    injectCustomCss()
    // Update page title
    if (statusPage.value.name) {
      document.title = `${statusPage.value.name} - Status Page`
    }
  }
})

onMounted(() => {
  loadStatusPage()
})
</script>

<style scoped>
.response-time-chart-container {
  width: 100%;
  overflow-x: auto;
}

.response-time-chart-container > div {
  width: 100%;
  min-width: 800px;
}

/* Ensure response time chart container doesn't overflow */
.response-time-chart-container {
  width: 100%;
  overflow-x: auto;
}

.response-time-chart-container::-webkit-scrollbar {
  height: 4px;
}

.response-time-chart-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.response-time-chart-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.response-time-chart-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
